"use client";
import { useSnackbarContext } from "@repo/contexts/SnackbarContext";
import { usePayByApplePay } from "@repo/lib/hooks/checkout";
import { useAddPaymentInfoEvent } from "@repo/lib/hooks/gtm";
import { useTrackPaymentInitiated } from "@repo/lib/hooks/webengage";
import {
  beginCheckout,
  createOrderCookie,
  placeOrder,
  processPayment,
} from "@repo/server-actions/checkout";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback, useMemo, useState, useTransition } from "react";

const PAYMENT_METHODS = {
  APPLEPAY: "APPLEPAY",
  PAYFORT: "PAYFORT",
  CHECKOUT_COM_FLOW: "CHECKOUT_COM_FLOW",
};

const usePayment = (
  selectedAddress,
  cart,
  locale,
  paymentMethods,
  isCkoPayWithFlow
) => {
  // Select CHECKOUT_COM_FLOW method selected or the first payment method coming from an API
  const defaultMethodSelection = useMemo(
    () =>
      isCkoPayWithFlow
        ? PAYMENT_METHODS.CHECKOUT_COM_FLOW
        : paymentMethods?.[0]?.code || "",
    [isCkoPayWithFlow, paymentMethods]
  );
  const [payFortDetails, setPayFortDetails] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    defaultMethodSelection
  );

  const { cartTotal } = cart;
  const isPayfort = selectedPaymentMethod === PAYMENT_METHODS.PAYFORT;
  const isApplePay = selectedPaymentMethod === PAYMENT_METHODS.APPLEPAY;

  const router = useRouter();
  const { setSnackbar } = useSnackbarContext();
  const { handleAddPaymentInfoEvent } = useAddPaymentInfoEvent();
  const [isPending, startTransition] = useTransition();
  const t = useTranslations("Common");
  const { trackPaymentInitiatedEvent } = useTrackPaymentInitiated();
  const { payByApplePay } = usePayByApplePay();

  const beginPlaceOrder = useCallback(
    (tokenizedData) => {
      const payLoad = selectedAddress;
      const { data } = tokenizedData || {};
      const ckoToken = data?.token;
      handleAddPaymentInfoEvent(cart, selectedPaymentMethod);
      trackPaymentInitiatedEvent(cart, selectedPaymentMethod);
      startTransition(async () => {
        try {
          if (isApplePay) {
            payByApplePay(cartTotal, payLoad, selectedPaymentMethod);
          } else {
            localStorage.setItem("paymentMethod", selectedPaymentMethod);
            // Begin checkout process
            const beginOrderProcess = await beginCheckout(locale);
            if (!beginOrderProcess.success) {
              setSnackbar({
                open: true,
                message:
                  beginOrderProcess?.message ||
                  t("Something_Went_Wrong_Please_Try_Again"),
                type: "error",
              });
              return;
            }
            // if beginOrderProcess is successfull then place order to OMS to get the order id
            const order = await placeOrder(
              payLoad,
              cartTotal,
              selectedPaymentMethod,
              locale
            );
            // Dislpay snackbar if placeOrder is failing
            if (!order?.orderId) {
              setSnackbar({
                open: true,
                message:
                  order?.message || t("Something_Went_Wrong_Please_Try_Again"),
                type: "error",
              });
              return;
            }
            // Create the order cookie
            await createOrderCookie(order?.orderId);
            // Process the Payment API to get the payment url and redirect to payment provider
            const processPay = await processPayment(
              selectedPaymentMethod,
              locale,
              ckoToken,
              isCkoPayWithFlow
            );
            if (!processPay?.data) {
              // Display the error message if the processPayment api is reurning error
              setSnackbar({
                open: true,
                message:
                  processPay?.message ||
                  t("Something_Went_Wrong_Please_Try_Again"),
                type: "error",
              });
              return;
            }
            const transactionData = {
              authorizationId: processPay.data.authorizationId,
              transactionId: processPay.data.transactionId,
            };
            // Getting the auth ID and transaction ID from process pay response and setting in session to be passed to
            // process order API payload to get order status data
            sessionStorage.setItem(
              "transactionData",
              JSON.stringify(transactionData)
            );
            if (isPayfort) {
              setPayFortDetails(processPay?.data);
            } else {
              router.push(processPay?.data?.url);
            }
          }
        } catch (error) {
          console.log(error);
          setSnackbar({
            open: true,
            message: t("Something_Went_Wrong_Please_Try_Again"),
            type: "error",
          });
        }
      });
    },
    [
      selectedAddress,
      handleAddPaymentInfoEvent,
      cart,
      selectedPaymentMethod,
      locale,
      cartTotal,
      isPayfort,
      router,
      setSnackbar,
      t,
      isCkoPayWithFlow,
      trackPaymentInitiatedEvent,
      payByApplePay,
      isApplePay,
    ]
  );

  const handlePlaceOrder = useCallback(
    (e) => {
      if (!isApplePay) {
        e.preventDefault();
      }
      beginPlaceOrder();
    },
    [beginPlaceOrder, isApplePay]
  );

  return {
    handlePlaceOrder,
    isPending,
    isPayfort,
    payFortDetails,
    selectedPaymentMethod,
    setSelectedPaymentMethod,
    beginPlaceOrder,
    isApplePay,
  };
};

export default usePayment;
